import React from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import PropTypes from "prop-types";
import Tab from "@mui/material/Tab";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { InfoOutlined, Menu, OpenInNew, TableRows } from "@mui/icons-material";
import { pageAccessAllowed } from "../../../api_calls/userManagement";
import { HtmlTooltip, HtmlTooltipDisabled, StyledDarkRedInformationTypography, StyledRightAlignedStack, StyledTabs } from "../../../components/commonComponents/StyledComponents/styled";
import ActionButton, { LightGreyButton } from "../../../components/commonComponents/Buttons/ActionButton";
import { updateStateData } from "../../../Store/actions";
import BrandReportFilter from "../ModalContent/BrandReportFilter";
import { generateRandomNumber, isBrandReportShareable } from "../../../api_calls/utils";
import Filters from "./Filters";
import ReportView from "./ReportView";
import ReportFilter from "../ModalContent/ReportFilter/ReportFilter";
import { resetShareModalInfo } from "../../../api_calls/ReportSettings";

// to load tab content
function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const AnalyticsDashboard = ({ reportSettingArr, viewMode, preview }) => {
    const [value, setValue] = React.useState(0);
    const { id } = useParams();
    const navigate = useNavigate();
    const campaignDetails = useSelector((state) => state.userSelections);
    const checkEditAccess = pageAccessAllowed(["campaign_list_can_edit"], []);
    const dispatchAction = useDispatch();
    const [showBrandFilter, setBrandFilterPopup] = React.useState(false);
    const [isTabsVisible, setIsTabsVisible] = React.useState(true); // State to manage tabs visibility
    const [scrollPos, setScrollPos] = React.useState(0);
    const [isToggleIconVisible, setIsToggleIconVisible] = React.useState(false);

    // const reportTabsConfig = campaign_analytics_tabs_config(viewMode);

    const tabsConfig = reportSettingArr;

    let reportTabsConfig = tabsConfig.length !== 0 ? tabsConfig.map(tab => {
        return {
            label: tab.name,
            unique_key: tab.name + "_" + generateRandomNumber(5),
            dashboard_id: tab.id_embeddable,
            filter_config: tab.filters,
            tab_type: tab.type,
        }
    }) : [];

    /*
      Brand View / Brand Report buttons follow the same rule as the share icon in the campaign list,
      so a campaign with only the impressions report ready can be shared from here as well.
      The published flag is additionally required for performance-only reports, because those tabs
      are only part of the report once the retailer has published them.
    */
    const campaignDetail = campaignDetails.campaign_detail;
    const reportPublished = campaignDetails.report_settings?.published === true;
    const impressionsReportReady = campaignDetail?.impression_report_status === 1;
    const brandReportActionsVisible =
        checkEditAccess.success === true &&
        isBrandReportShareable(campaignDetail) &&
        (impressionsReportReady || reportPublished);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const toggleTabsVisibility = () => {
        setIsTabsVisible(!isTabsVisible);
    };


    React.useEffect(() => {
        const handleScroll = () => {
            const currentScrollPos = window.pageYOffset;
            // Show tabs when at the top of the page
            if (currentScrollPos === 0) {
                setIsTabsVisible(true);
                setIsToggleIconVisible(false);
            }
            // Hide tabs on scroll down
            /*
             * currentScrollPos > 24 : specifically added to pass the cypress test because it scrolls a
             * bit down while validating the exsitance of brand report button and according to
             * implemented functionality, those buttons will be hidden as we start scrolling down the
             * page which is why the cypress test was failed. So added a check that buttons will be
             * hidden when current scroll position is above 25.
             */
            if (currentScrollPos > 24 && currentScrollPos > scrollPos) {
                setIsToggleIconVisible(true);
                setIsTabsVisible(false);
            }
            setScrollPos(currentScrollPos);
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [scrollPos]);

    return (
        <>
            {reportTabsConfig.length !== 0 && (
                <>
                    <Box
                        width="100%"
                        sx={{
                            position: "sticky",
                            top: preview === true ? "-20px" : 0,
                            zIndex: 999,
                            backgroundColor: "#fff",
                            borderBottom: 1,
                            borderColor: "divider",
                        }}
                    >
                        {
                            preview === true && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        mb: 1,
                                        mt: preview === true ? 1 : 0,
                                    }}
                                >
                                    <Chip
                                        label="Preview Mode"
                                        sx={{
                                            backgroundColor: "#AF3241",
                                            color: "white",
                                            fontWeight: "bold",
                                            width: "200px",
                                        }}
                                    />
                                </Box>
                            )
                        }
                        {/* ---------- report tabs and brand report/view buttons ------- */}
                        {isTabsVisible && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: "#fff",
                                }}
                            >
                                <Box component={"div"} sx={{ marginTop: "5px" }}>
                                    <StyledTabs value={value} onChange={handleChange} aria-label="brand analytics" textColor={"#F04B55"}>
                                        {reportTabsConfig.map((tabDetail) => {
                                            // icon={tabDetail.icon}
                                            return (
                                                <Tab
                                                    key={tabDetail.unique_key}
                                                    aria-label={tabDetail.label}
                                                    label={tabDetail.label}
                                                    {...a11yProps(0)}
                                                />
                                            );
                                        })}
                                    </StyledTabs>
                                </Box>

                                {/* ----------- brand report buttons ----------- */}
                                <Box
                                    component={"div"}
                                    sx={{
                                        flex: 1,
                                        marginTop: "7px",
                                        marginBottom: "13px",
                                    }}
                                >
                                    {brandReportActionsVisible && (
                                        <StyledRightAlignedStack direction={"row"} spacing={2}>
                                            <LightGreyButton
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    // this button will be disabled in preview mode
                                                    if (preview === false) {
                                                        dispatchAction(
                                                            updateStateData({
                                                                brand_report_view: viewMode === "Brand" ? false : true,
                                                            })
                                                        );

                                                        if (viewMode === "Brand") {
                                                            navigate("/check-campaigns/campaign-analytics/" + id);
                                                        } else {
                                                            navigate("/check-campaigns/brand-report-view/" + id);
                                                        }
                                                    }
                                                }}
                                                label={viewMode === "Brand" ? "Retailer View" : "Brand View"}
                                                icon={<></>}
                                                selected={false}
                                            />
                                            <ActionButton
                                                disabled={preview ? true : false}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    // this button will be disabled in preview mode
                                                    if (preview === false) {
                                                        setBrandFilterPopup(!showBrandFilter);
                                                    }
                                                }}
                                                label={"Brand Report"}
                                                icon={<></>}
                                            />
                                        </StyledRightAlignedStack>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                    {/* ----------- filters section ------------ */}
                    <Box
                        width={"100%"}
                        sx={{
                            display: "flex", // Make the Box a flex container
                            // borderBottom: 1,
                            // borderColor: "divider",
                            position: "sticky",
                            flexWrap: "wrap",
                            top: preview === true ? 82 : isTabsVisible ? 54 : 0,
                            zIndex: 999,
                            backgroundColor: "#fff",
                        }}
                    >
                        <div>
                            {/* Toggle Button */}
                            {isToggleIconVisible && campaignDetails.campaign_detail.campaign_status_code === 4 && (
                                <IconButton
                                    sx={{
                                        top: window.screen.width < 1300 ? "0px" : "25px",
                                        left: "2px",
                                        zIndex: 999,
                                    }}
                                >
                                    <TableRows sx={{ zIndex: 999 }} onClick={toggleTabsVisibility} />
                                </IconButton>
                            )}
                        </div>
                        {/* -----------  Dashboard filters ----------- */}
                        <Box
                            component={"div"}
                            width={"100%"}
                            sx={{
                                flex: 1,
                                pr: "10px",
                            }}
                        >
                            <Filters
                                configArr={reportTabsConfig[value].filter_config}
                                configTabName={reportTabsConfig[value].label}
                                configTabType={reportTabsConfig[value].tab_type}
                            />
                        </Box>
                        <Box
                            component={"div"}
                            width={"100%"}
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                pr: "25px",
                                // mt: 0.5,
                                mb: 1,
                            }}
                        >
                            <HtmlTooltipDisabled title="Open methodology" arrow>
                                <Box
                                    component={"a"}
                                    href="https://methodik.cyreen.de"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Open methodology"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: "8px",
                                        color: "#62626E",
                                        textDecoration: "none",
                                        fontWeight: 700,
                                        fontSize: "0.875rem",
                                        lineHeight: "1.71429",
                                        transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                                        "& svg": {
                                            color: "inherit",
                                        },
                                        "&:hover": {
                                            backgroundColor: "#62626E",
                                            color: "#fff",
                                        },
                                    }}
                                >
                                    <InfoOutlined sx={{ fontSize: 15 }} />
                                    <Typography variant="body2" sx={{ 
                                        fontWeight: "bold",
                                    }}>How this data is calculated</Typography>
                                    <OpenInNew sx={{ fontSize: 14 }} />
                                </Box>
                            </HtmlTooltipDisabled>
                        </Box>
                    </Box>

                    {/* ============== tab contents =========== */}
                    {reportTabsConfig.length !== 0 &&
                        reportTabsConfig.map((tabDetail, index) => {
                            return (
                                <CustomTabPanel value={value} index={index}>
                                    {/* {tabDetail.dashboard} */}
                                    <ReportView
                                        configTabName={tabDetail.label}
                                        configTabType={tabDetail.tab_type}
                                        configFilterArr={tabDetail.filter_config}
                                        configEmbeddableId={tabDetail.dashboard_id}
                                        viewMode={viewMode}
                                    />
                                </CustomTabPanel>
                            );
                        })}

                    {showBrandFilter && (
                        <ReportFilter
                            open={showBrandFilter}
                            onClose={() => {
                                setBrandFilterPopup(!showBrandFilter);
                                resetShareModalInfo();
                            }}
                            size={"md"}
                            closeModal={() => {
                                setBrandFilterPopup(!showBrandFilter);
                                resetShareModalInfo();
                            }}
                            usedFor={"update"}
                            campaignRecord={typeof campaignDetails.campaign_detail !== "undefined" ? campaignDetails.campaign_detail : {}}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default AnalyticsDashboard;
